import type { IBusinessUnit } from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const BASE = '/api/v1/business-units';

export async function fetchBusinessUnits(): Promise<IBusinessUnit[]> {
  const res = await apiFetch(BASE);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch business units: ${res.status}`));
  return res.json() as Promise<IBusinessUnit[]>;
}

// Active-only counterpart of fetchBusinessUnits() — the shared data source for every Business
// Unit picker. fetchBusinessUnits() stays reserved for the admin list page and for list-page
// filters, which must keep showing inactive units.
export async function fetchBusinessUnitLookup(): Promise<IBusinessUnit[]> {
  const res = await apiFetch(`${BASE}/lookup`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch business units: ${res.status}`));
  return res.json() as Promise<IBusinessUnit[]>;
}

export async function fetchBusinessUnit(id: string): Promise<IBusinessUnit> {
  const res = await apiFetch(`${BASE}/${id}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch business unit: ${res.status}`));
  return res.json() as Promise<IBusinessUnit>;
}

export async function createBusinessUnit(data: {
  name: string;
  description?: string | null;
  departmentId: string;
  // `| null` only so the same payload object literal BusinessUnitsPage builds can be passed to
  // either create or update — a `null` Head on create is equivalent to omitting it entirely.
  headId?: string | null;
}): Promise<IBusinessUnit> {
  const res = await apiFetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create business unit'));
  return res.json() as Promise<IBusinessUnit>;
}

export async function updateBusinessUnit(
  id: string,
  // headId: string | null — see updateSalesRegion's identical comment for why.
  data: Partial<{ name: string; description?: string | null; departmentId: string; headId?: string | null }>,
): Promise<IBusinessUnit> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update business unit'));
  return res.json() as Promise<IBusinessUnit>;
}

export async function deactivateBusinessUnit(id: string): Promise<IBusinessUnit> {
  const res = await apiFetch(`${BASE}/${id}/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to deactivate business unit'));
  return res.json() as Promise<IBusinessUnit>;
}

export async function activateBusinessUnit(id: string): Promise<IBusinessUnit> {
  const res = await apiFetch(`${BASE}/${id}/activate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to activate business unit'));
  return res.json() as Promise<IBusinessUnit>;
}
